<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Email Drafts</h1>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
        <button
          v-if="selectedEmails.length > 0"
          @click="approveSelected"
          :disabled="bulkApproving"
          class="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
        >
          <svg v-if="bulkApproving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-if="bulkApproving">Approving...</span>
          <span v-else>Approve Selected ({{ selectedEmails.length }})</span>
        </button>
        <button
          v-if="selectedEmails.length > 0"
          @click="deleteSelected"
          class="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
        >
          Delete Selected ({{ selectedEmails.length }})
        </button>
        <div class="relative inline-block text-left">
          <button
            @click="showGenerateMenu = !showGenerateMenu"
            class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            Generate Email
            <svg class="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <div
            v-if="showGenerateMenu"
            class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
          >
            <div class="py-1">
              <button
                @click="openGenerateDrawer('prompt')"
                class="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                Generate from Prompt
              </button>
              <button
                @click="openGenerateDrawer('article')"
                class="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                Generate from Article
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Generation Drawer -->
    <EmailGenerationDrawer
      :is-open="showGenerateDrawer"
      :generation-type="generationType"
      :use-json-mode="isTestingJsonMode"
      @close="closeGenerateDrawer"
      @success="handleGenerateSuccess"
    />

    <!-- Emails List -->
    <div class="mt-8 flex flex-col">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-4 lg:flex lg:justify-end lg:space-x-4">
          <!-- Search Field -->
          <div class="relative w-64">
            <input
              v-model="searchInput"
              type="text"
              placeholder="Search emails (3+ characters)..."
              class="block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
              @input="handleSearchInput"
            />
            <div v-if="isSearching" class="absolute right-2 top-3">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
            </div>
          </div>

          <!-- Content Type Filter -->
          <Listbox v-model="selectedContentType" @update:modelValue="fetchEmails">
            <div class="relative mt-1 w-48">
              <ListboxButton class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                <span class="block truncate">{{ selectedContentType ? formatContentType(selectedContentType) : 'All Sources' }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption v-slot="{ active, selected }" :value="''" as="template">
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">All Sources</span>
                    </li>
                  </ListboxOption>
                  <ListboxOption
                    v-for="contentType in ['prompt', 'article', 'manual']"
                    :key="contentType"
                    :value="contentType"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ formatContentType(contentType) }}</span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>

          <Listbox v-model="selectedInstitutionId" @update:modelValue="fetchEmails">
            <div class="relative mt-1 w-64">
              <ListboxButton class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                <span class="block truncate">{{ selectedInstitutionName || 'All Institutions' }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption v-slot="{ active, selected }" :value="''" as="template">
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">All Institutions</span>
                    </li>
                  </ListboxOption>
                  <ListboxOption
                    v-for="inst in institutions"
                    :key="inst.id"
                    :value="inst.id"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ inst.name }}</span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>

          <Listbox v-model="selectedStatus" @update:modelValue="fetchEmails">
            <div class="relative mt-1 w-48">
              <ListboxButton class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                <span class="block truncate">{{ selectedStatus || 'All Statuses' }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption v-slot="{ active, selected }" :value="''" as="template">
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">All Statuses</span>
                    </li>
                  </ListboxOption>
                  <ListboxOption
                    v-for="status in ['draft', 'sent']"
                    :key="status"
                    :value="status"
                    v-slot="{ active, selected }"
                    as="template"
                    @click="fetchEmails"
                  >
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ status.charAt(0).toUpperCase() + status.slice(1) }}</span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>
        <div class="inline-block min-w-full py-2 align-middle">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8 w-[5%]">
                  <input
                    type="checkbox"
                    :checked="selectedEmails.length === emails.length && emails.length > 0"
                    :indeterminate="selectedEmails.length > 0 && selectedEmails.length < emails.length"
                    @change="toggleAllEmails"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  >
                </th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8 w-[25%]">
                  Subject
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-[25%]">
                  Recipient
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-[15%]">
                  Status
                </th>
                <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 w-[30%]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr v-for="(email, index) in emails" :key="email.id" class="hover:bg-gray-50">
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 lg:pl-8">
                  <input
                    type="checkbox"
                    :checked="selectedEmails.includes(email.id)"
                    @click="toggleEmailSelection($event, email.id, index)"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  >
                </td>
                <td class="whitespace-normal py-4 pl-4 pr-3 text-sm sm:pl-6 lg:pl-8">
                  <div class="max-w-lg">
                    <div class="truncate" :title="email.subject">
                      <span v-if="email.review_note" class="mr-1" :title="email.review_note" aria-label="Has a note for the reviewer">📝</span>
                      {{ email.subject }}
                    </div>
                  </div>
                </td>
                <td class="whitespace-normal px-3 py-4 text-sm text-gray-500">
                  <div class="max-w-lg">
                    <div class="truncate" :title="email.recipient_email">
                      {{ email.recipient_name }}
                    </div>
                    <div class="text-xs text-gray-400 truncate" :title="email.recipient_email">
                      {{ email.recipient_email }}
                    </div>
                  </div>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span :class="[
                    email.status === 'sent' ? 'bg-green-100 text-green-800' : 
                    email.status === 'failed' ? 'bg-red-100 text-red-800' : 
                    email.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800',
                    'inline-flex rounded-full px-2 text-xs font-semibold leading-5'
                  ]">
                    {{ email.status }}
                    <span v-if="email.status === 'sent' && email.sent_at" class="sent-date-tag ml-1">
                      {{ formatDate(email.sent_at) }}
                    </span>
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-right">
                  <div class="flex justify-end items-center gap-1">
                    <button 
                      @click="openEmailPreview(email)"
                      class="text-indigo-600 hover:text-indigo-900 px-1.5 py-0.5">
                      View
                    </button>
                    <button 
                      v-if="email.status === 'draft'"
                      @click="approveEmail(email)"
                      class="text-green-600 hover:text-green-900 px-1.5 py-0.5">
                      Approve
                    </button>
                    <button 
                      @click="deleteEmail(email)"
                      class="text-red-600 hover:text-red-900 px-1.5 py-0.5">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div class="mt-4 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing <span class="font-medium">{{ emails.length }}</span> of <span class="font-medium">{{ totalItems }}</span> results
        </div>
        <div class="flex justify-center space-x-1">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-2 py-1 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <!-- First page -->
          <button
            v-if="totalPages > 5 && currentPage > 3"
            @click="changePage(1)"
            class="px-2 py-1 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            1
          </button>
          
          <!-- Ellipsis if needed -->
          <span
            v-if="totalPages > 5 && currentPage > 3"
            class="px-2 py-1 text-sm text-gray-500"
          >
            ...
          </span>
          
          <!-- Page numbers -->
          <template v-for="page in totalPages" :key="page">
            <!-- Show current page, and 2 pages before and after when possible -->
            <button
              v-if="page === 1 || page === totalPages || 
                  (currentPage - 2 <= page && page <= currentPage + 2)"
              @click="changePage(page)"
              :class="[
                'px-2 py-1 rounded-md text-sm',
                currentPage === page
                  ? 'bg-indigo-600 text-white font-medium border border-indigo-600'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
          </template>
          
          <!-- Ellipsis if needed -->
          <span
            v-if="totalPages > 5 && currentPage < totalPages - 2"
            class="px-2 py-1 text-sm text-gray-500"
          >
            ...
          </span>
          
          <!-- Last page -->
          <button
            v-if="totalPages > 5 && currentPage < totalPages - 2"
            @click="changePage(totalPages)"
            class="px-2 py-1 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {{ totalPages }}
          </button>
          
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages || totalPages === 0"
            class="px-2 py-1 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div class="flex items-center">
          <span class="text-sm text-gray-700 mr-2">Page size:</span>
          <select 
            v-model="pageSize" 
            @change="currentPage = 1; fetchEmails()"
            class="rounded-md border-gray-300 py-1 text-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Email Preview Modal -->
    <div v-if="selectedEmail" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-lg font-medium">Email Preview</h3>
            <button @click="selectedEmail = null" class="text-gray-400 hover:text-gray-500">
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="space-y-4">
            <div v-if="selectedEmail.review_note" class="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
              <strong>Note for the reviewer (not sent):</strong> {{ selectedEmail.review_note }}
            </div>
            <div v-if="selectedEmail.status === 'draft' && selectedEmail.error_message" class="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {{ selectedEmail.error_message }}
            </div>
            <p v-if="selectedEmail.reply_by" class="text-sm text-gray-600">Answer expected by {{ selectedEmail.reply_by }}.</p>
            <EmailDetails 
              :email="selectedEmail" 
              @refresh="fetchEmails"
            />
            
            <div class="flex justify-end space-x-3">
              <button
                @click="selectedEmail = null"
                class="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
              
              <div v-if="selectedEmail.status === 'draft'" class="flex space-x-3">
                <button
                  @click="saveEmailChanges"
                  class="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Save Changes
                </button>
                <button
                  @click="approveEmail(selectedEmail)"
                  class="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  Approve & Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Improve Email Dialog -->
    <div v-if="showImproveDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-lg font-medium">Improve Email Content</h3>
            <button @click="showImproveDialog = false" class="text-gray-400 hover:text-gray-500">
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Instructions for improvement</label>
              <textarea
                v-model="improvePrompt"
                rows="4"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter your instructions for improving the email content..."
              ></textarea>
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button 
              @click="showImproveDialog = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
              Cancel
            </button>
            <button 
              @click="improveEmail"
              :disabled="improving"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-50">
              {{ improving ? 'Improving...' : 'Improve' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading Overlay for bulk approval -->
  <div v-if="bulkApproving" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
      <div class="flex items-center">
        <svg class="animate-spin -ml-1 mr-3 h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div>
          <p class="text-lg font-medium text-gray-900">Approving Emails</p>
          <p class="text-sm text-gray-500">Please wait while we approve {{ selectedEmails.length }} email(s)...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import EmailPreviewModal from '@/components/emails/EmailPreviewModal.vue'
import EmailImproveDialog from '@/components/emails/EmailImproveDialog.vue'
import EmailGenerationDrawer from '@/components/emails/EmailGenerationDrawer.vue'
import PaginationControls from '@/components/common/PaginationControls.vue'
import apiClient from '@/api/client'
import { EMAILS } from '@/api/endpoints'
import { useInstitutions } from '@/composables/useInstitutions'
import { useEmailGeneration } from '@/composables/useEmailGeneration'
import EmailDetails from '@/components/email/EmailDetails.vue'
import { useEventBus, emailEvents } from '@/utils/eventBus'

interface Email {
  id: string
  subject: string
  body: string
  recipient_email: string
  recipient_name: string
  recipients: string[]
  review_note?: string | null
  reply_by?: string | null
  error_message?: string | null
  content_id: string
  status: 'draft' | 'sent' | 'failed'
  created_at: string
  updated_at: string
  metadata?: {
    analysis: any
    institutions: string[]
    sender_profile_id?: string
  }
  sent_at?: string
}

const emails = ref<Email[]>([])
const selectedEmail = ref<Email | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const showImproveDialog = ref(false)
const improvePrompt = ref('')
const improving = ref(false)
const bulkApproving = ref(false)
const showGenerateDrawer = ref(false)
const showGenerateMenu = ref(false)
const generationType = ref<'prompt' | 'article' | null>(null)
const isTestingJsonMode = ref(false)
const selectedInstitutionId = ref('')
const selectedStatus = ref('')
const selectedContentType = ref('')
const searchInput = ref('')
const isSearching = ref(false)
let searchTimeout: number | null = null
const { institutions, fetchInstitutions } = useInstitutions()
const { generateEmail } = useEmailGeneration()
const { on: onEvent } = useEventBus()
const generating = ref(false)
const selectedEmails = ref<string[]>([])
const lastSelectedIndex = ref<number | null>(null)

// Pagination variables
const currentPage = ref(1)
const pageSize = ref(100)
const totalItems = ref(0)
const totalPages = ref(0)

// Update polling logic to only poll when needed
let pollInterval: number | null = null

// Computed property to display the selected institution name
const selectedInstitutionName = computed(() => {
  if (!selectedInstitutionId.value) return ''
  const found = institutions.value.find(inst => inst.id === selectedInstitutionId.value)
  return found ? found.name : ''
})

function formatDate(date: string | undefined): string {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString()
}

function formatContentType(contentType: string): string {
  const types = {
    'prompt': 'From Prompt',
    'article': 'From Article',
    'manual': 'Manual/Direct'
  }
  return types[contentType] || contentType
}

function handleSearchInput() {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  // Clear search if less than 3 characters
  if (searchInput.value.length < 3) {
    if (searchInput.value.length === 0) {
      // Reset to first page and fetch without search
      currentPage.value = 1
      fetchEmails()
    }
    return
  }
  
  isSearching.value = true
  
  searchTimeout = setTimeout(() => {
    currentPage.value = 1 // Reset to first page when searching
    fetchEmails()
    isSearching.value = false
  }, 300)
}

function openEmailPreview(email: Email) {
  selectedEmail.value = email
}

async function approveEmail(email: Email) {
  try {
    // If we're editing the email in the modal, save changes first
    if (selectedEmail.value?.id === email.id) {
      await apiClient.put(EMAILS.UPDATE(email.id), {
        subject: selectedEmail.value.subject,
        body: selectedEmail.value.body
      })
    }
    
    // Then approve and send
    await apiClient.post(EMAILS.APPROVE(email.id))
    
    // Notify other components that emails were approved/sent
    emailEvents.emailsSent([email.id])
    
    await fetchEmails()
    if (selectedEmail.value?.id === email.id) {
      selectedEmail.value = null
    }
  } catch (error: any) {
    console.error('Error approving email:', error)
    alert(error.response?.data?.detail || error.response?.data?.message || 'Failed to approve email')
  }
}

async function fetchEmails() {
  loading.value = true;
  error.value = null;
  try {
    const params: any = {
      page: currentPage.value,
      limit: pageSize.value
    };
    if (selectedInstitutionId.value) {
      params.institution_id = selectedInstitutionId.value;
    }
    if (selectedStatus.value) {
      params.status = selectedStatus.value;
    }
    if (searchInput.value && searchInput.value.length >= 3) {
      params.search = searchInput.value;
    }
    if (selectedContentType.value) {
      params.content_type = selectedContentType.value;
    }
    const response = await apiClient.get(EMAILS.LIST, { params });
    emails.value = response.data.items;
    totalItems.value = response.data.total;
    totalPages.value = response.data.total_pages;
  } catch (error: any) {
    console.error('Error fetching emails:', error);
    error.value = error.response?.data?.message || 'Failed to fetch emails';
  } finally {
    loading.value = false;
  }
}

function changePage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchEmails();
}

async function startPolling() {
  // Only start polling if we're not already polling
  if (pollInterval) return
  
  // Poll every 30 seconds instead of 5
  pollInterval = window.setInterval(async () => {
    // Only fetch if we're not already loading
    if (!loading.value) {
      await fetchEmails()
    }
  }, 30000) // 30 seconds
}

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

// handleVisibilityChange is defined later in the file

function openImproveDialog() {
  showImproveDialog.value = true
  improvePrompt.value = ''
}

async function improveEmail() {
  if (!selectedEmail.value || !improvePrompt.value) return
  
  improving.value = true
  try {
    const response = await apiClient.post(EMAILS.IMPROVE(selectedEmail.value.id), {
      prompt: improvePrompt.value
    })
    
    // Update the email content with improved version
    selectedEmail.value = {
      ...selectedEmail.value,
      ...response.data.content
    }
    
    // Close the improve dialog
    showImproveDialog.value = false
    improvePrompt.value = ''
    
    // Refresh the emails list
    await fetchEmails()
  } catch (error: any) {
    console.error('Error improving email:', error)
    alert(error.response?.data?.message || 'Failed to improve email')
  } finally {
    improving.value = false
  }
}

async function deleteEmail(email: Email) {
  if (!confirm('Are you sure you want to delete this email?')) return
  
  try {
    await apiClient.delete(EMAILS.DELETE(email.id))
    await fetchEmails()
    if (selectedEmail.value?.id === email.id) {
      selectedEmail.value = null
    }
  } catch (error: any) {
    console.error('Error deleting email:', error)
    alert(error.response?.data?.message || 'Failed to delete email')
  }
}

function openGenerateDrawer(type: 'prompt' | 'article', isJsonTest: boolean = false) {
  generationType.value = type
  isTestingJsonMode.value = isJsonTest
  showGenerateDrawer.value = true
  showGenerateMenu.value = false
}

function closeGenerateDrawer() {
  showGenerateDrawer.value = false
  generationType.value = null
  isTestingJsonMode.value = false
}

function handleGenerateSuccess() {
  showGenerateDrawer.value = false
  
  // Notify that new emails were generated (which will also invalidate cache)
  emailEvents.invalidateCache()
  
  fetchEmails() // Refresh the email list
}

function toggleEmailSelection(event: MouseEvent, emailId: string, index: number) {
  if (event.shiftKey && lastSelectedIndex.value !== null) {
    // Handle shift-click
    const start = Math.min(lastSelectedIndex.value, index)
    const end = Math.max(lastSelectedIndex.value, index)
    
    const emailsInRange = emails.value.slice(start, end + 1).map(e => e.id)
    
    // If the last selected email was selected, select all in range
    // If it was deselected, deselect all in range
    const shouldSelect = selectedEmails.value.includes(emails.value[lastSelectedIndex.value].id)
    
    if (shouldSelect) {
      // Add all emails in range that aren't already selected
      emailsInRange.forEach(id => {
        if (!selectedEmails.value.includes(id)) {
          selectedEmails.value.push(id)
        }
      })
    } else {
      // Remove all emails in range
      selectedEmails.value = selectedEmails.value.filter(id => !emailsInRange.includes(id))
    }
  } else {
    // Handle normal click
    const index = selectedEmails.value.indexOf(emailId)
    if (index === -1) {
      selectedEmails.value.push(emailId)
    } else {
      selectedEmails.value.splice(index, 1)
    }
  }
  
  lastSelectedIndex.value = index
}

function toggleAllEmails(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.checked) {
    selectedEmails.value = emails.value.map(email => email.id)
  } else {
    selectedEmails.value = []
  }
  lastSelectedIndex.value = null
}

async function approveSelected() {
  if (!confirm(`Are you sure you want to approve ${selectedEmails.value.length} emails?\n\nThey go out 1–2 minutes apart, not all at once.`)) return
  
  bulkApproving.value = true
  try {
    const response = await apiClient.post(EMAILS.APPROVE_BULK, {
      email_ids: selectedEmails.value
    })
    const refused = (response.data?.results || []).filter((r: any) => r.status === 'error')
    if (refused.length) {
      alert(`${refused.length} of ${selectedEmails.value.length} not sent:\n\n` + refused.map((r: any) => r.error).join('\n'))
    }
    
    // Notify other components that emails were approved/sent
    emailEvents.emailsSent([...selectedEmails.value])
    
    await fetchEmails()
    selectedEmails.value = []
    lastSelectedIndex.value = null
  } catch (error: any) {
    console.error('Error approving emails:', error)
    alert(error.response?.data?.detail || error.response?.data?.message || 'Failed to approve emails')
  } finally {
    bulkApproving.value = false
  }
}

async function deleteSelected() {
  if (!confirm(`Are you sure you want to delete ${selectedEmails.value.length} emails?`)) return
  
  try {
    const deletePromises = selectedEmails.value.map(emailId => 
      apiClient.delete(EMAILS.DELETE(emailId))
    )
    
    await Promise.all(deletePromises)
    await fetchEmails()
    selectedEmails.value = []
    lastSelectedIndex.value = null
  } catch (error: any) {
    console.error('Error deleting emails:', error)
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to delete emails'
    alert(errorMessage)
  }
}

async function saveEmailChanges() {
  if (!selectedEmail.value) return
  
  try {
    await apiClient.put(EMAILS.UPDATE(selectedEmail.value.id), {
      subject: selectedEmail.value.subject,
      body: selectedEmail.value.body
    })
    
    // Refresh the emails list
    await fetchEmails()
    alert('Changes saved successfully!')
  } catch (error: any) {
    console.error('Error saving email changes:', error)
    alert(error.response?.data?.message || 'Failed to save changes')
  }
}

// Setup event listeners for cache invalidation
let unsubscribeEmailsInvalidate: (() => void) | null = null

// Check localStorage for cache invalidation flag
function checkCacheInvalidationFlag() {
  const invalidateFlag = localStorage.getItem('emails_cache_invalidate')
  if (invalidateFlag) {
    console.log('[EmailsView] Found cache invalidation flag in localStorage, refreshing...')
    localStorage.removeItem('emails_cache_invalidate') // Clear the flag
    fetchEmails()
  }
}

// Handle page visibility changes
function handleVisibilityChange() {
  if (document.hidden) {
    stopPolling()
  } else {
    startPolling()
    console.log('[EmailsView] Page became visible, checking for cache invalidation...')
    // Check cache invalidation flag when user returns to the tab
    checkCacheInvalidationFlag()
    
    // Regular refresh when not loading
    if (!loading.value) {
      fetchEmails()
    }
  }
}

// Fetch initial data on mount
onMounted(() => {
  fetchInstitutions() // Fetch institutions when component mounts
  fetchEmails()
  startPolling()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  
  // Check cache invalidation flag on mount
  checkCacheInvalidationFlag()
  
  // Listen for email cache invalidation events
  console.log('[EmailsView] Setting up cache invalidation listener...')
  unsubscribeEmailsInvalidate = onEvent('emails:invalidate', () => {
    console.log('[EmailsView] Received cache invalidation event, refreshing emails...')
    fetchEmails()
  })
  console.log('[EmailsView] Cache invalidation listener setup complete')
})

// Cleanup on unmount
onUnmounted(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  // Cleanup event listeners
  if (unsubscribeEmailsInvalidate) {
    unsubscribeEmailsInvalidate()
  }
})

// Watch for institution changes to refetch emails
watch(selectedInstitutionId, () => {
  currentPage.value = 1 // Reset to first page when filter changes
  fetchEmails()
})

// Watch for status changes to refetch emails
watch(selectedStatus, () => {
  currentPage.value = 1 // Reset to first page when filter changes
  fetchEmails()
})

// Watch for content type changes to refetch emails  
watch(selectedContentType, () => {
  currentPage.value = 1 // Reset to first page when filter changes
  fetchEmails()
})
</script>

<style scoped>
/* Add custom styles for table layout */
table {
  table-layout: fixed;
}

.max-w-lg {
  @apply overflow-hidden;
}

.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}

/* Add styles for the Actions column to ensure buttons fit */
.actions {
  min-width: 180px;
}

/* Style for the sent date tag */
.sent-date-tag {
  font-size: 0.7rem;
  opacity: 0.9;
  font-weight: normal;
}
</style> 